/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment settings form validation
 * @author Satyaki Bose
 * @file assignmentSettingsFormValidation.js
 */

import {
  BASE_VALUE_MAX,
  BASE_VALUE_MIN,
  GRADEBOOK_DISPLAY_MODE,
  LATE_WORK_DEADLINE_TYPE,
  LATE_WORK_PERCENT_MAX,
  LATE_WORK_PERCENT_MIN
} from './assignmentSettingsConstants';
import {
  formatInputDateFromStorage,
  isValidMaskedDateInput
} from './lateWorkCustomDateInputUtils';
import { parsePositiveNumber } from './assignmentSettingsFormUtils';


/** Validation helper for the base value field */
export function createBaseValueValidator(textInfo, visibility = {}) {
  return (value, formValues) => {
    if (!visibility.isEqualWeightingBaseValueVisible) {
      return true;
    }
    if (formValues.gradebookDisplayMode !== GRADEBOOK_DISPLAY_MODE.EQUAL_WEIGHTING) {
      return true;
    }
    const parsed = parsePositiveNumber(value);
    if (parsed === null) {
      return textInfo.baseValueRequired;
    }
    if (parsed < BASE_VALUE_MIN || parsed > BASE_VALUE_MAX) {
      return textInfo.baseValueInvalid;
    }

    return true;
  };
}

/** Validation helper for the late work percent field */
export function createLateWorkPercentValidator(textInfo, visibility = {}) {
  return (value, formValues) => {
    if (!visibility.isLateWorkPercentFieldVisible) {
      return true;
    }
    if (!formValues.allowLateWork) {
      return true;
    }
    const parsed = parsePositiveNumber(value);
    if (parsed === null) {
      return textInfo.lateWorkPercentRequired;
    }
    if (parsed < LATE_WORK_PERCENT_MIN || parsed > LATE_WORK_PERCENT_MAX) {
      return textInfo.lateWorkPercentInvalid;
    }

    return true;
  };
}

/** Validation helper for the late work custom date field */
export function createLateWorkCustomDateValidator(textInfo, visibility = {}) {
  return (value, formValues) => {
    if (!visibility.isLateWorkCustomDateFieldVisible) {
      return true;
    }
    if (!formValues.allowLateWork) {
      return true;
    }
    if (formValues.lateWorkDeadlineType !== LATE_WORK_DEADLINE_TYPE.CUSTOM) {
      return true;
    }
    if (!value) {
      return textInfo.customDateRequired;
    }
    const maskedValue = formatInputDateFromStorage(value);
    if (!isValidMaskedDateInput(maskedValue, { minDate: new Date() })) {
      return textInfo.customDateInvalid || textInfo.customDateRequired;
    }

    return true;
  };
}
